import { useCallback, useState } from 'react';
import {
  AppliedFiltersState,
  ScenarioResultFiltersType,
} from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-results-filters.type';
import { kpis, SELECT_ALL_KPI_ID } from 'views/bluviu-nutrition/features/scenarios-v2/utils/compare-result-utils';

const DEFAULT_KPI_IDS = kpis.filter((k) => k.id !== SELECT_ALL_KPI_ID).map((k) => k.id);

interface UseCompareFiltersOptions {
  initialFeedPrograms?: string[];
  initialResultsFor?: string[];
}

const createDefaultFilters = (programs: string[], resultsFor: string[]): ScenarioResultFiltersType => ({
  pendingFeedPrograms: programs,
  pendingKPIs: DEFAULT_KPI_IDS,
  pendingResultsFor: resultsFor,
});

const createDefaultApplied = (programs: string[], resultsFor: string[]): AppliedFiltersState => ({
  appliedFeedPrograms: programs,
  appliedKPIs: DEFAULT_KPI_IDS,
  appliedResultsFor: resultsFor,
});

export const useCompareFilters = (options?: UseCompareFiltersOptions) => {
  const { initialFeedPrograms = [], initialResultsFor = [] } = options || {};

  const [filters, setFilters] = useState<ScenarioResultFiltersType>(() =>
    createDefaultFilters(initialFeedPrograms, initialResultsFor)
  );
  const [appliedFilters, setAppliedFilters] = useState<AppliedFiltersState>(() =>
    createDefaultApplied(initialFeedPrograms, initialResultsFor)
  );

  const updateFilters = useCallback((newFilters: Partial<ScenarioResultFiltersType>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  }, []);

  const updateImmediateFilters = useCallback((newFilters: Partial<ScenarioResultFiltersType>) => {
    setFilters((prev) => {
      const nextFilters = { ...prev, ...newFilters };

      setAppliedFilters((currentApplied) => ({
        ...currentApplied,
        ...(newFilters.pendingFeedPrograms ? { appliedFeedPrograms: nextFilters.pendingFeedPrograms } : {}),
        ...(newFilters.pendingKPIs ? { appliedKPIs: nextFilters.pendingKPIs } : {}),
        ...(newFilters.pendingResultsFor ? { appliedResultsFor: nextFilters.pendingResultsFor } : {}),
      }));

      return nextFilters;
    });
  }, []);

  const resetFilters = useCallback((programs: string[] = [], resultsFor: string[] = []) => {
    setFilters(createDefaultFilters(programs, resultsFor));
    setAppliedFilters(createDefaultApplied(programs, resultsFor));
  }, []);

  const initializeFeedPrograms = useCallback((programs: string[]) => {
    setFilters((prev) => ({ ...prev, pendingFeedPrograms: programs }));
    setAppliedFilters((prev) => ({ ...prev, appliedFeedPrograms: programs }));
  }, []);

  const initializeResultsFor = useCallback((resultsFor: string[]) => {
    setFilters((prev) => ({ ...prev, pendingResultsFor: resultsFor }));
    setAppliedFilters((prev) => ({ ...prev, appliedResultsFor: resultsFor }));
  }, []);

  const applyFilters = useCallback(() => {
    setAppliedFilters({
      appliedFeedPrograms: filters.pendingFeedPrograms,
      appliedKPIs: filters.pendingKPIs,
      appliedResultsFor: filters.pendingResultsFor,
    });
  }, [filters]);

  return {
    filters,
    appliedFilters,
    updateFilters,
    updateImmediateFilters,
    resetFilters,
    initializeFeedPrograms,
    initializeResultsFor,
    applyFilters,
  };
};
